import { login, registerAxiosInterceptor } from "@trustpilot/businessapp-login";
import { ThemeProvider, WidePage } from "@trustpilot/businessapp-patternlibrary";
import { ConstellationProvider, Locales } from "@trustpilot/constellation-components";
import axios from "axios";
import { LocalizationProvider } from "ilenia";
import { createRoot } from "react-dom/client";

import { getBaseData } from "./api";
import App from "./app";
import { SomethingWentWrong } from "./components/system";
import configuration from "./configuration";
import { ALL_BUSINESS_ACCOUNTS_HAVE_SSO_ENFORCED } from "./constants";
import "./styles/index.scss";
import { ISSSOAUTHENTICATED_COOKIE, getCookieByName } from "./utils/cookies";
import { resolveBrowserBusinessUnitId, resolveBrowserLocale } from "./utils/locale";
import { logout } from "./utils/logout";
import { getTranslations } from "./utils/translations";

const browserLocale = resolveBrowserLocale();
const browserBusinessUnitId = resolveBrowserBusinessUnitId();

const firstLoadSessionStorageKey = "b2b-first-page-loaded";
const isFirstLoad = !sessionStorage.getItem(firstLoadSessionStorageKey);
const container = document.getElementById("shell-root");
const loaderId = `page-loader-${isFirstLoad ? "full" : "view"}`;
const loaderElement = document.getElementById(loaderId);

const root = createRoot(container);

const waitForPageLoader = async (then: number): Promise<void> => {
  const loaderMinimumDuration = 500;
  let now = new Date().getTime();

  while (now - then < loaderMinimumDuration) {
    now = new Date().getTime();
    await new Promise((r) => setTimeout(r, 200));
  }

  return;
};

async function renderAppshell() {
  if (isFirstLoad) {
    sessionStorage.setItem(firstLoadSessionStorageKey, "false");
  }

  loaderElement.classList.add(`${loaderId}--visible`);
  const then = new Date().getTime();

  try {
    await login(configuration);
    registerAxiosInterceptor(axios);

    const isSsoSamlAuthenticated = getCookieByName(ISSSOAUTHENTICATED_COOKIE);
    const { businessUser, businessUnit, salesforceId, locale, launchDarkly } = await getBaseData({
      locale: browserLocale,
      businessUnitId: browserBusinessUnitId,
      isSsoSamlAuthenticated: isSsoSamlAuthenticated === "true",
    });

    //[TODO] there's a weird bug when running on strict mode in React v18. Seems like init cookie consent in the AnalyticsProvider
    // brings some trouble, you can see for example that the const Routes = React.memo() starts to render twice.
    // The same happens with something inside <Shell {...data} />, once you comment those two things out the app behaves as normal. WTF
    // maybe this explains? https://coderpad.io/blog/development/why-react-18-broke-your-app/

    root.render(
      // <React.StrictMode>
      <App
        businessUser={businessUser}
        businessUnit={businessUnit}
        salesforceId={salesforceId}
        locale={locale}
        launchDarkly={launchDarkly}
      />,
      // </React.StrictMode>
    );

    // If this is the first page load in the current user session and the loader animation hasn't finished before the data is loaded
    // wait for the animation to be completed.
    if (isFirstLoad) {
      await waitForPageLoader(then);
    }
  } catch (e) {
    if (e === ALL_BUSINESS_ACCOUNTS_HAVE_SSO_ENFORCED) {
      const redirectionReason = encodeURIComponent("SSO login required");
      return logout({ redirectionReason }, "/sso-login");
    }

    const translations = getTranslations(browserLocale);
    root.render(
      <LocalizationProvider locale={browserLocale} translations={translations}>
        <ConstellationProvider locale={browserLocale as Locales}>
          <ThemeProvider>
            <WidePage>
              <SomethingWentWrong />
            </WidePage>
          </ThemeProvider>
        </ConstellationProvider>
      </LocalizationProvider>,
    );
  } finally {
    loaderElement.classList.remove(`${loaderId}--visible`);
  }
}

renderAppshell();
